import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import cls from 'classnames'
import styles from './DropdownMenu.module.less'

type Align = 'start' | 'center' | 'end'

type DropdownMenuContextValue = {
  open: boolean
  setOpen: (open: boolean) => void
  openOnHover: boolean
  hoverCloseDelay: number
  scheduleClose: () => void
  cancelClose: () => void
  openOriginRef: React.MutableRefObject<'hover' | 'click' | 'keyboard' | null>
  triggerRef: React.RefObject<HTMLElement>
  contentRef: React.RefObject<HTMLDivElement>
}

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(null)

function useDropdownMenuContext(componentName: string) {
  const context = useContext(DropdownMenuContext)
  if (!context) {
    throw new Error(`${componentName} must be used within DropdownMenu`)
  }
  return context
}

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    refs.forEach((ref) => {
      if (!ref) return
      if (typeof ref === 'function') {
        ref(node)
      } else {
        ; (ref as React.MutableRefObject<T | null>).current = node
      }
    })
  }
}

type DropdownMenuProps = {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  openOnHover?: boolean
  hoverCloseDelay?: number
  children: React.ReactNode
}

const DropdownMenu = ({
  open,
  defaultOpen,
  onOpenChange,
  openOnHover = false,
  hoverCloseDelay = 120,
  children,
}: DropdownMenuProps) => {
  const [internalOpen, setInternalOpen] = useState(!!defaultOpen)
  const triggerRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const hoverCloseTimerRef = useRef<number | null>(null)
  const openOriginRef = useRef<'hover' | 'click' | 'keyboard' | null>(null)

  const isOpen = open ?? internalOpen

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (open === undefined) {
        setInternalOpen(nextOpen)
      }
      onOpenChange?.(nextOpen)
    },
    [onOpenChange, open],
  )

  const cancelClose = useCallback(() => {
    if (hoverCloseTimerRef.current === null) return
    window.clearTimeout(hoverCloseTimerRef.current)
    hoverCloseTimerRef.current = null
  }, [])

  const scheduleClose = useCallback(() => {
    cancelClose()
    hoverCloseTimerRef.current = window.setTimeout(() => {
      setOpen(false)
    }, hoverCloseDelay)
  }, [cancelClose, hoverCloseDelay, setOpen])

  useEffect(() => {
    return () => cancelClose()
  }, [cancelClose])

  useEffect(() => {
    if (!isOpen) return
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target)) return
      if (contentRef.current?.contains(target)) return
      setOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, setOpen])

  const contextValue = useMemo<DropdownMenuContextValue>(
    () => ({
      open: isOpen,
      setOpen,
      openOnHover,
      hoverCloseDelay,
      scheduleClose,
      cancelClose,
      openOriginRef,
      triggerRef,
      contentRef,
    }),
    [isOpen, setOpen, openOnHover, hoverCloseDelay, scheduleClose, cancelClose],
  )

  return <DropdownMenuContext.Provider value={contextValue}>{children}</DropdownMenuContext.Provider>
}

type SlotProps = {
  children: React.ReactElement
  className?: string
}

const Slot = React.forwardRef<HTMLElement, SlotProps & React.HTMLAttributes<HTMLElement>>(
  ({ children, className, ...rest }, ref) => {
    if (!React.isValidElement(children)) {
      return null
    }
    const child = children as React.ReactElement<any>
    return React.cloneElement(child, {
      ...(rest as Record<string, unknown>),
      className: cls(child.props.className, className),
      ref: mergeRefs(ref, (child as { ref?: React.Ref<HTMLElement> }).ref),
    } as any)
  },
)
Slot.displayName = 'Slot'

type DropdownMenuTriggerProps = React.HTMLAttributes<HTMLElement> & {
  asChild?: boolean
}

const DropdownMenuTrigger = React.forwardRef<HTMLElement, DropdownMenuTriggerProps>(
  ({ className, children, asChild, onClick, onKeyDown, onMouseEnter, onMouseLeave, ...rest }, ref) => {
    const { open, setOpen, triggerRef, openOnHover, scheduleClose, cancelClose, openOriginRef } =
      useDropdownMenuContext('DropdownMenuTrigger')
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      onClick?.(event)
      if (!event.defaultPrevented) {
        openOriginRef.current = 'click'
        setOpen(!open)
      }
    }
    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
      onKeyDown?.(event)
      if (event.defaultPrevented) return
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        openOriginRef.current = 'keyboard'
        setOpen(!open)
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        openOriginRef.current = 'keyboard'
        setOpen(true)
      }
    }
    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
      onMouseEnter?.(event)
      if (event.defaultPrevented || !openOnHover) return
      cancelClose()
      openOriginRef.current = 'hover'
      setOpen(true)
    }
    const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
      onMouseLeave?.(event)
      if (event.defaultPrevented || !openOnHover) return
      scheduleClose()
    }
    const sharedProps: React.HTMLAttributes<HTMLElement> & { 'data-state'?: 'open' | 'closed' } = {
      className: cls(styles.dropdownTrigger, className),
      'aria-haspopup': 'menu',
      'aria-expanded': open,
      'data-state': open ? 'open' : 'closed',
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ...rest,
    }

    if (asChild && React.isValidElement(children)) {
      return (
        <Slot ref={mergeRefs(ref, triggerRef)} {...sharedProps}>
          {children}
        </Slot>
      )
    }

    return (
      <button
        ref={mergeRefs<HTMLButtonElement>(
          ref as React.Ref<HTMLButtonElement>,
          triggerRef as unknown as React.Ref<HTMLButtonElement>,
        )}
        type="button"
        {...sharedProps}
      >
        {children}
      </button>
    )
  },
)
DropdownMenuTrigger.displayName = 'DropdownMenuTrigger'

type DropdownMenuContentProps = React.HTMLAttributes<HTMLDivElement> & {
  sideOffset?: number
  align?: Align
  forceMount?: boolean
}

const DropdownMenuContent = ({
  className,
  children,
  sideOffset = 8,
  align = 'start',
  forceMount,
  onKeyDown,
  onMouseEnter,
  onMouseLeave,
  style,
  ...rest
}: DropdownMenuContentProps) => {
  const { open, triggerRef, contentRef, setOpen, openOnHover, scheduleClose, cancelClose, openOriginRef } =
    useDropdownMenuContext('DropdownMenuContent')
  const [positionStyle, setPositionStyle] = useState<React.CSSProperties>({})

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    const content = contentRef.current
    if (!trigger || !content) return
    const triggerRect = trigger.getBoundingClientRect()
    const contentRect = content.getBoundingClientRect()
    let left = triggerRect.left
    if (align === 'center') {
      left = triggerRect.left + (triggerRect.width - contentRect.width) / 2
    }
    if (align === 'end') {
      left = triggerRect.right - contentRect.width
    }
    const top = triggerRect.bottom + sideOffset
    setPositionStyle({ top, left, minWidth: triggerRect.width })
  }, [align, sideOffset, triggerRef, contentRef])

  useLayoutEffect(() => {
    if (!open) return
    updatePosition()
    const frame = requestAnimationFrame(updatePosition)
    return () => cancelAnimationFrame(frame)
  }, [open, updatePosition, children])

  useEffect(() => {
    if (!open) return
    const handleResize = () => updatePosition()
    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleResize, true)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleResize, true)
    }
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return
    if (openOriginRef.current === 'hover') return
    const content = contentRef.current
    if (!content) return
    const firstItem = content.querySelector<HTMLElement>('[data-dropdown-item="true"]:not([aria-disabled])')
    firstItem?.focus()
  }, [open, contentRef, openOriginRef])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event)
    if (event.defaultPrevented) return
    const items = Array.from(
      contentRef.current?.querySelectorAll<HTMLElement>('[data-dropdown-item="true"]:not([aria-disabled])') || [],
    )
    if (!items.length) return
    const currentIndex = items.findIndex((item) => item === document.activeElement)
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      items[Math.min(currentIndex + 1, items.length - 1)]?.focus()
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      items[Math.max(currentIndex - 1, 0)]?.focus()
    }
    if (event.key === 'Home') {
      event.preventDefault()
      items[0]?.focus()
    }
    if (event.key === 'End') {
      event.preventDefault()
      items[items.length - 1]?.focus()
    }
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    }
  }

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    onMouseEnter?.(event)
    if (event.defaultPrevented || !openOnHover) return
    cancelClose()
  }

  const handleMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    onMouseLeave?.(event)
    if (event.defaultPrevented || !openOnHover) return
    scheduleClose()
  }

  if (!open && !forceMount) return null

  return (
    <div
      ref={contentRef}
      className={cls(styles.dropdownContent, className)}
      role="menu"
      data-state={open ? 'open' : 'closed'}
      style={{ ...positionStyle, ...style }}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      {children}
    </div>
  )
}

type DropdownMenuItemProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onSelect'> & {
  inset?: boolean
  onSelect?: (event: React.MouseEvent<HTMLButtonElement>) => void
}

const DropdownMenuItem = React.forwardRef<HTMLButtonElement, DropdownMenuItemProps>(
  ({ className, inset, disabled, onClick, onSelect, role, ...rest }, ref) => {
    const { setOpen } = useDropdownMenuContext('DropdownMenuItem')
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return
      onClick?.(event)
      onSelect?.(event)
      if (!event.defaultPrevented) {
        setOpen(false)
      }
    }
    return (
      <button
        ref={ref}
        type="button"
        role={role ?? 'menuitem'}
        className={cls(styles.dropdownItem, inset && styles.inset, className)}
        data-dropdown-item="true"
        aria-disabled={disabled || undefined}
        disabled={disabled}
        tabIndex={-1}
        onClick={handleClick}
        {...rest}
      />
    )
  },
)
DropdownMenuItem.displayName = 'DropdownMenuItem'

type DropdownMenuLabelProps = React.HTMLAttributes<HTMLDivElement> & {
  inset?: boolean
}

const DropdownMenuLabel = ({ className, inset, ...rest }: DropdownMenuLabelProps) => {
  return <div className={cls(styles.dropdownLabel, inset && styles.inset, className)} {...rest} />
}

type DropdownMenuSeparatorProps = React.HTMLAttributes<HTMLDivElement>

const DropdownMenuSeparator = ({ className, ...rest }: DropdownMenuSeparatorProps) => {
  return <div className={cls(styles.dropdownSeparator, className)} role="separator" {...rest} />
}

type DropdownMenuGroupProps = React.HTMLAttributes<HTMLDivElement>

const DropdownMenuGroup = ({ className, ...rest }: DropdownMenuGroupProps) => {
  return <div className={cls(styles.dropdownGroup, className)} {...rest} />
}

type DropdownMenuCheckboxItemProps = Omit<DropdownMenuItemProps, 'onSelect'> & {
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
}

const DropdownMenuCheckboxItem = React.forwardRef<HTMLButtonElement, DropdownMenuCheckboxItemProps>(
  ({ className, checked, onCheckedChange, children, ...rest }, ref) => {
    const handleSelect = () => {
      onCheckedChange?.(!checked)
    }
    return (
      <DropdownMenuItem
        ref={ref}
        className={cls(styles.dropdownCheckboxItem, className)}
        role="menuitemcheckbox"
        aria-checked={checked}
        onClick={handleSelect}
        {...rest}
      >
        <span className={styles.itemIndicator} aria-hidden>
          {checked ? <CheckIcon /> : null}
        </span>
        {children}
      </DropdownMenuItem>
    )
  },
)
DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem'

type DropdownMenuRadioGroupContextValue = {
  value?: string
  onValueChange?: (value: string) => void
}

const DropdownMenuRadioGroupContext = createContext<DropdownMenuRadioGroupContextValue | null>(null)

type DropdownMenuRadioGroupProps = React.HTMLAttributes<HTMLDivElement> & {
  value?: string
  onValueChange?: (value: string) => void
}

const DropdownMenuRadioGroup = ({ className, value, onValueChange, ...rest }: DropdownMenuRadioGroupProps) => {
  const contextValue = useMemo(() => ({ value, onValueChange }), [value, onValueChange])
  return (
    <DropdownMenuRadioGroupContext.Provider value={contextValue}>
      <div className={cls(styles.dropdownGroup, className)} {...rest} />
    </DropdownMenuRadioGroupContext.Provider>
  )
}

type DropdownMenuRadioItemProps = Omit<DropdownMenuItemProps, 'onSelect'> & {
  value: string
}

const DropdownMenuRadioItem = React.forwardRef<HTMLButtonElement, DropdownMenuRadioItemProps>(
  ({ className, value, children, ...rest }, ref) => {
    const group = useContext(DropdownMenuRadioGroupContext)
    const checked = group?.value === value
    const handleSelect = () => {
      group?.onValueChange?.(value)
    }
    return (
      <DropdownMenuItem
        ref={ref}
        className={cls(styles.dropdownRadioItem, className)}
        role="menuitemradio"
        aria-checked={checked}
        onClick={handleSelect}
        {...rest}
      >
        <span className={styles.itemIndicator} aria-hidden>
          {checked ? <DotIcon /> : null}
        </span>
        {children}
      </DropdownMenuItem>
    )
  },
)
DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem'

type DropdownMenuSubContextValue = {
  open: boolean
  setOpen: (open: boolean) => void
  triggerRef: React.RefObject<HTMLElement>
  contentRef: React.RefObject<HTMLDivElement>
}

const DropdownMenuSubContext = createContext<DropdownMenuSubContextValue | null>(null)

function useDropdownMenuSubContext(componentName: string) {
  const context = useContext(DropdownMenuSubContext)
  if (!context) {
    throw new Error(`${componentName} must be used within DropdownMenuSub`)
  }
  return context
}

type DropdownMenuSubProps = {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
  className?: string
}

const DropdownMenuSub = ({ open, defaultOpen, onOpenChange, children, className }: DropdownMenuSubProps) => {
  const parent = useDropdownMenuContext('DropdownMenuSub')
  const [internalOpen, setInternalOpen] = useState(!!defaultOpen)
  const triggerRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const isOpen = open ?? internalOpen

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (open === undefined) {
        setInternalOpen(nextOpen)
      }
      onOpenChange?.(nextOpen)
    },
    [onOpenChange, open],
  )

  useEffect(() => {
    if (!parent.open) {
      setOpen(false)
    }
  }, [parent.open, setOpen])

  const contextValue = useMemo(
    () => ({
      open: isOpen,
      setOpen,
      triggerRef,
      contentRef,
    }),
    [isOpen, setOpen],
  )

  return (
    <DropdownMenuSubContext.Provider value={contextValue}>
      <div
        className={cls(styles.dropdownSub, className)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        {children}
      </div>
    </DropdownMenuSubContext.Provider>
  )
}

type DropdownMenuSubTriggerProps = React.HTMLAttributes<HTMLElement> & {
  inset?: boolean
  asChild?: boolean
}

const DropdownMenuSubTrigger = React.forwardRef<HTMLElement, DropdownMenuSubTriggerProps>(
  ({ className, children, inset, asChild, onClick, onKeyDown, ...rest }, ref) => {
    const { open, setOpen, triggerRef } = useDropdownMenuSubContext('DropdownMenuSubTrigger')
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      onClick?.(event)
      if (!event.defaultPrevented) {
        setOpen(!open)
      }
    }
    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
      onKeyDown?.(event)
      if (event.defaultPrevented) return
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        setOpen(true)
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        setOpen(false)
      }
    }
    const sharedProps: React.HTMLAttributes<HTMLElement> & { 'data-dropdown-item'?: 'true' } = {
      className: cls(styles.dropdownSubTrigger, inset && styles.inset, className),
      'aria-haspopup': 'menu',
      'aria-expanded': open,
      'data-dropdown-item': 'true',
      tabIndex: -1,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      ...rest,
    }

    if (asChild && React.isValidElement(children)) {
      return (
        <Slot ref={mergeRefs(ref, triggerRef)} {...sharedProps}>
          {children}
        </Slot>
      )
    }

    return (
      <button
        ref={mergeRefs<HTMLButtonElement>(
          ref as React.Ref<HTMLButtonElement>,
          triggerRef as unknown as React.Ref<HTMLButtonElement>,
        )}
        type="button"
        {...sharedProps}
      >
        <span className={styles.subTriggerText}>{children}</span>
        <span className={styles.subTriggerIcon} aria-hidden>
          <ChevronRightIcon />
        </span>
      </button>
    )
  },
)
DropdownMenuSubTrigger.displayName = 'DropdownMenuSubTrigger'

type DropdownMenuSubContentProps = React.HTMLAttributes<HTMLDivElement> & {
  sideOffset?: number
  alignOffset?: number
  forceMount?: boolean
}

const DropdownMenuSubContent = ({
  className,
  children,
  sideOffset = 4,
  alignOffset = -6,
  forceMount,
  style,
  ...rest
}: DropdownMenuSubContentProps) => {
  const { open, triggerRef, contentRef } = useDropdownMenuSubContext('DropdownMenuSubContent')
  const [positionStyle, setPositionStyle] = useState<React.CSSProperties>({})

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    const content = contentRef.current
    if (!trigger || !content) return
    const triggerRect = trigger.getBoundingClientRect()
    const contentRect = content.getBoundingClientRect()
    const left = triggerRect.right + sideOffset
    const top = triggerRect.top + alignOffset
    setPositionStyle({ top, left, minWidth: contentRect.width })
  }, [alignOffset, sideOffset, triggerRef, contentRef])

  useLayoutEffect(() => {
    if (!open) return
    updatePosition()
    const frame = requestAnimationFrame(updatePosition)
    return () => cancelAnimationFrame(frame)
  }, [open, updatePosition, children])

  useEffect(() => {
    if (!open) return
    const handleResize = () => updatePosition()
    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleResize, true)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleResize, true)
    }
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return
    const content = contentRef.current
    if (!content) return
    const firstItem = content.querySelector<HTMLElement>('[data-dropdown-item="true"]:not([aria-disabled])')
    firstItem?.focus()
  }, [open, contentRef])

  if (!open && !forceMount) return null

  return (
    <div
      ref={contentRef}
      className={cls(styles.dropdownSubContent, className)}
      role="menu"
      data-state={open ? 'open' : 'closed'}
      style={{ ...positionStyle, ...style }}
      {...rest}
    >
      {children}
    </div>
  )
}

type DropdownMenuShortcutProps = React.HTMLAttributes<HTMLSpanElement>

const DropdownMenuShortcut = ({ className, ...rest }: DropdownMenuShortcutProps) => {
  return <span className={cls(styles.dropdownShortcut, className)} {...rest} />
}

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" className={styles.icon} aria-hidden>
    <path d="M6 10.2l2.4 2.4L14 7" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const DotIcon = () => (
  <svg viewBox="0 0 20 20" className={styles.icon} aria-hidden>
    <circle cx="10" cy="10" r="3.5" fill="currentColor" />
  </svg>
)

const ChevronRightIcon = () => (
  <svg viewBox="0 0 20 20" className={styles.icon} aria-hidden>
    <path d="M8 5.5l4.5 4.5L8 14.5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuShortcut,
}
